import { useQueries } from "@tanstack/react-query";

import { useApiClient } from "@/api/ApiContext";
import {
  manualQueryKeys,
  manualRefetchInterval,
  readManualState,
  type PreviewManualSessionState,
} from "@/api/previewManual";
import { useCapability } from "@/auth/SessionContext";

/**
 * The manual state of several runs at once, body-free. Same key and semantics
 * as `usePreviewManualSessionQuery` (404 is data: `null` = not a manual
 * session), so it dedupes with the selected run's own observer. `undefined` =
 * not answered yet.
 */
export function useLabManualStatuses(
  projectId: string,
  refs: readonly string[],
): ReadonlyMap<string, PreviewManualSessionState | null | undefined> {
  const client = useApiClient();
  const inspect = useCapability("preview.inspect");
  return useQueries({
    queries: refs.map((previewRunRef) => ({
      queryKey: manualQueryKeys.session(projectId, previewRunRef),
      queryFn: () => readManualState(client, projectId, previewRunRef),
      enabled: inspect.allowed && projectId.length > 0,
      staleTime: 0,
      gcTime: 0,
      retry: false,
      refetchInterval: (query: {
        state: {
          status: string;
          data: PreviewManualSessionState | null | undefined;
        };
      }) => manualRefetchInterval(query.state),
    })),
    combine: (results) =>
      new Map(refs.map((ref, index) => [ref, results[index]?.data] as const)),
  });
}
